/**
 * usePublishDraft Hook
 * 
 * Provides publish functionality for draft workflows.
 * Publishes the current draft to production with full validation.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useDraftModeStore } from '@/stores/draftModeStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { publishDraft } from '@/services/draftWorkflowApi';
import { cleanWorkflowForSerialization, nonEmptyObject } from '@/services/workflowSerializer';
import { saveNodesMetadata } from '@/services/nodesMetadataApi';
import { commitSidecarPlan, planPublishSidecar } from '@/services/nodesMetadataCache';
import { useWorkflowLayoutStore } from '@/stores/workflowLayoutStore';
import { telemetryService } from '@/services/telemetryService';
import { trackFunnelStep, FunnelNames } from '@/services/telemetryFunnels';
import { isWorkflowEmpty, findAgentActionsMissingMessage } from '@/utils/validation';
import type { PublishResult } from '@/types/draftMode';
import type { ConnectionsConfiguration } from '@/types/logicApps';
import type { WorkflowDefinition } from '@/types/workflowDefinition';
import { workflowDraftStatusesQueryPrefix } from './useWorkflowDraftStatuses';
import { workflowGraphDefinitionQueryPrefix } from './useWorkflowGraphDefinitions';
import { getBlockingCodeIssue } from './useIsDraftDirty';

// Variables passed through useMutation so onSuccess/onError receive a stable
// per-call snapshot (no refs, no aliasing across concurrent publishes).
interface PublishVariables {
  wasRepublish: boolean;
}

interface PublishSnapshot {
  workflow: WorkflowDefinition;
  connections: ConnectionsConfiguration | null;
  parameters: Record<string, unknown> | null;
  notes: Record<string, unknown> | null;
  workflowChangeCount: number;
  connectionChangeCount: number;
}

export interface PublishCallbackInfo {
  /**
   * Whether this publish was a "republish" — i.e. draft == published at click
   * time. Snapshotted before any post-publish store mutations so it always
   * reflects the user's intent at the moment they clicked Publish.
   */
  wasRepublish: boolean;
}

export interface UsePublishDraftOptions {
  /** Workflow name to publish */
  workflowName: string;
  /** Callback on successful publish */
  onSuccess?: (info: PublishCallbackInfo) => void;
  /** Callback on publish error */
  onError?: (error: Error, info: PublishCallbackInfo) => void;
}

export interface UsePublishDraftResult {
  /** Trigger the publish operation */
  publish: () => Promise<PublishResult>;
  /** Whether a publish is in progress */
  isPublishing: boolean;
  /** Last publish error, if any */
  error: string | null;
}

/**
 * Hook to publish the current draft workflow to production
 */
export function usePublishDraft({
  workflowName,
  onSuccess,
  onError,
}: UsePublishDraftOptions): UsePublishDraftResult {
  const queryClient = useQueryClient();
  const { getOttoToken } = useAuth();
  const { selectedProject, selectedApp } = useAppStore();
  
  const [error, setError] = useState<string | null>(null);
  const publishStartTimeRef = useRef<number>(0);
  const publishSnapshotRef = useRef<PublishSnapshot | null>(null);

  // Get workflow state
  const workflow = useWorkflowStore((state) => state.workflow);
  const markClean = useWorkflowStore((state) => state.markClean);

  // Get draft mode store actions
  const setIsPublishing = useDraftModeStore((state) => state.setIsPublishing);
  const setProdArtifacts = useDraftModeStore((state) => state.setProdArtifacts);
  const discardDraft = useDraftModeStore((state) => state.discardDraft);
  // Get connections for saving alongside workflow
  const exportConnections = useConnectionStore((state) => state.exportToConnectionsConfig);

  // Mutation for publishing draft workflow
  const mutation = useMutation<PublishResult, Error, PublishVariables>({
    mutationFn: async (): Promise<PublishResult> => {
      publishStartTimeRef.current = performance.now();

      const blockingCodeIssue = getBlockingCodeIssue();
      if (blockingCodeIssue) {
        telemetryService.trackEvent('workflow.publish.blocked', {
          reason: blockingCodeIssue.reason,
        });
        throw new Error(blockingCodeIssue.message);
      }

      const currentWorkflow = useWorkflowStore.getState().workflow;
      if (!currentWorkflow) {
        throw new Error('No workflow to publish');
      }

      if (!selectedProject || !selectedApp) {
        throw new Error('No Logic App selected');
      }

      setIsPublishing(true);

      const currentConnections = exportConnections();
      const currentDraftState = useDraftModeStore.getState();
      publishSnapshotRef.current = {
        workflow: structuredClone(currentWorkflow),
        connections: currentConnections
          ? structuredClone(currentConnections)
          : null,
        parameters: currentDraftState.draftParameters
          ? structuredClone(currentDraftState.draftParameters)
          : null,
        notes: currentDraftState.draftNotes
          ? structuredClone(currentDraftState.draftNotes)
          : null,
        workflowChangeCount: useWorkflowStore.getState().changeCount,
        connectionChangeCount: useConnectionStore.getState().changeCount,
      };
      const snapshot = publishSnapshotRef.current;

      // Build the workflow definition in the expected format.
      // Clean through the serializer to strip internal-only UI properties
      // (e.g., `detached`) and any legacy position metadata so the runtime
      // gets a clean, layout-free definition. Pixel positions live in the
      // client-side `workflowLayoutStore` instead.
      const cleanedWorkflow = cleanWorkflowForSerialization(
        snapshot.workflow,
        {},
      );
      const workflowNotes = nonEmptyObject(
        snapshot.workflow.notes as Record<string, unknown> | undefined,
      );
      const workflowDefinition = {
        definition: cleanedWorkflow.definition,
        kind: snapshot.workflow.kind || 'Stateful',
        ...(workflowNotes && { notes: workflowNotes }),
      };

      // Get connections config
      const connectionsConfig = snapshot.connections;

      const result = await publishDraft({
        projectName: selectedProject,
        appName: selectedApp.name,
        getToken: getOttoToken,
        workflowName,
        workflowDefinition,
        connectionsConfig: nonEmptyObject(connectionsConfig),
        parametersConfig: nonEmptyObject(snapshot.parameters),
        notesConfig: nonEmptyObject(snapshot.notes),
        isDraftMode: currentDraftState.isDraftMode,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to publish');
      }

      // Snapshot the just-published layout (draft branch → published branch)
      // and persist the sidecar. This is the moment the published view's
      // layout becomes meaningful — readers viewing the published workflow
      // expect the same arrangement the author just released.
      const identity = useWorkflowStore.getState().currentWorkflowIdentity;
      if (identity) {
        try {
          useWorkflowLayoutStore.getState().snapshotDraftToPublished(identity);
          const plan = planPublishSidecar(snapshot.workflow, identity);
          if (plan) {
            const ok = await saveNodesMetadata(
              {
                projectName: selectedProject,
                appName: selectedApp.name,
                getToken: getOttoToken,
                workflowName,
              },
              plan.file,
            );
            if (ok) commitSidecarPlan(plan);
          }
        } catch (err) {
          console.warn('[usePublishDraft] Failed to persist nodesMetadata sidecar', err);
        }
      }

      return result;
    },
    onSuccess: (_data, { wasRepublish }) => {
      const duration_ms = performance.now() - publishStartTimeRef.current;
      const snapshot = publishSnapshotRef.current;
      const changedDuringPublish =
        !snapshot ||
        useWorkflowStore.getState().changeCount !==
          snapshot.workflowChangeCount ||
        useConnectionStore.getState().changeCount !==
          snapshot.connectionChangeCount;

      setError(null);
      setIsPublishing(false);
      
      // Production reflects the exact snapshot sent to the server, never edits
      // that landed while the publish request was in flight.
      if (snapshot) {
        setProdArtifacts({
          workflow: snapshot.workflow,
          connections: snapshot.connections,
          parameters: snapshot.parameters,
          notes: snapshot.notes,
        });
      }

      if (!changedDuringPublish) {
        // Only collapse draft onto production when no newer edits exist.
        discardDraft();
        markClean();
      }

      // Invalidate workflowTriggers cache so TriggerWorkflowDialog and the
      // node editor's HTTP URL field fetch fresh data (callback URLs change
      // after publish). The runtime may not have the published trigger's
      // callback URL ready at the exact moment this mutation resolves, so we
      // schedule a couple of follow-up invalidations to catch the runtime
      // catching up — bounded so we don't poll indefinitely.
      //
      // Only the immediate invalidation is broad (so the run dialog also
      // refreshes); the delayed retries are scoped via predicate to ONLY the
      // per-trigger callback URL queries to avoid re-running the heavier
      // useWorkflowTriggers fan-out (which itself calls /listCallbackUrl once
      // per trigger) on every retry.
      const triggersKey = ['workflowTriggers', workflowName, selectedProject, selectedApp?.name];
      void queryClient.invalidateQueries({ queryKey: triggersKey });

      // The Parameters / Connections pages read the on-disk JSON via React
      // Query, not the draft store. Seed both prod and draft caches with what
      // was just published so observers (designer's useAppLevelParameters,
      // the Parameters/Connections page on next mount) reflect the new values
      // immediately. Using setQueryData (not invalidate) avoids a refetch
      // race with the backend's read-after-write window — we already know the
      // canonical post-publish value: it's what we just sent.
      const projectKey = selectedProject?.toLowerCase();
      const appKey = selectedApp?.name?.toLowerCase();
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['workflows', projectKey, appKey],
        }),
        queryClient.invalidateQueries({
          queryKey: workflowDraftStatusesQueryPrefix(
            selectedProject ?? undefined,
            selectedApp?.name ?? undefined,
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: workflowGraphDefinitionQueryPrefix(
            selectedProject ?? undefined,
            selectedApp?.name ?? undefined,
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: ['workflowDefinition', projectKey, appKey],
        }),
      ]);
      const seedPublished = <T>(baseKey: string, value: T) => {
        queryClient.setQueryData([baseKey, projectKey, appKey], value);
        if (!changedDuringPublish) {
          queryClient.setQueryData(
            [`${baseKey}-draft`, projectKey, appKey],
            value,
          );
        }
      };
      seedPublished('parameters', snapshot?.parameters ?? {});
      seedPublished('connections', snapshot?.connections ?? {});

      const isCallbackUrlQueryFor = (key: readonly unknown[]): boolean =>
        key.length >= 6 &&
        key[0] === 'workflowTriggers' &&
        key[1] === workflowName &&
        key[2] === selectedProject &&
        key[3] === selectedApp?.name &&
        key[4] === 'callback';

      window.setTimeout(() => {
        void queryClient.invalidateQueries({
          predicate: (q) => isCallbackUrlQueryFor(q.queryKey),
        });
      }, 1500);
      window.setTimeout(() => {
        void queryClient.invalidateQueries({
          predicate: (q) => isCallbackUrlQueryFor(q.queryKey),
        });
      }, 4000);

      telemetryService.trackEvent(
        'workflow.draft.published',
        { wasRepublish: String(wasRepublish) },
        { duration_ms },
      );
      trackFunnelStep(FunnelNames.WORKFLOW_DESIGN, 'draft_publish', 5, { duration_ms });

      onSuccess?.({ wasRepublish });
      publishSnapshotRef.current = null;
    },
    onError: (err, { wasRepublish }) => {
      console.error('[usePublishDraft] Publish failed:', err);
      setError(err.message);
      setIsPublishing(false);

      telemetryService.trackEvent('workflow.draft.publish_failed', {
        error: err.message,
        wasRepublish: String(wasRepublish),
      });

      onError?.(err, { wasRepublish });
      publishSnapshotRef.current = null;
    },
  });

  const publish = useCallback(async (): Promise<PublishResult> => {
    // Snapshot the republish flag at click time so onSuccess/onError see the
    // value the user intended, not the value after setProdArtifacts runs.
    const wasRepublish = useDraftModeStore.getState().areProdAndDraftEqual();

    if (!workflow || !selectedApp || !selectedProject) {
      const errorMsg = !workflow ? 'No workflow to publish' : 'No Logic App selected';
      console.error('[usePublishDraft] Publish pre-condition failed:', errorMsg);
      telemetryService.trackEvent('workflow.draft.publish_precondition_failed', { error: errorMsg });
      onError?.(new Error(errorMsg), { wasRepublish });
      return {
        success: false,
        error: errorMsg,
      };
    }

    // Block publishing an empty workflow (no triggers AND no actions). The
    // runtime would happily accept it, but a workflow with nothing to run
    // can't be invoked and only creates confusing prod state.
    if (isWorkflowEmpty(workflow)) {
      const errorMsg = 'Cannot publish an empty workflow. Add a trigger to get started.';
      console.error('[usePublishDraft] Publish pre-condition failed:', errorMsg);
      telemetryService.trackEvent('workflow.draft.publish_precondition_failed', { error: 'empty_workflow' });
      onError?.(new Error(errorMsg), { wasRepublish });
      return {
        success: false,
        error: errorMsg,
      };
    }

    // Block publishing an agent action with an empty prompt (no System instructions AND no User
    // message). The runtime otherwise rejects it with a cryptic "missing content" error; catch it
    // here and tell the author exactly what to fix (#3421). Check the serialized definition so we
    // see the same normalized messages the runtime would (the serializer keeps an intentional
    // blank User entry for hosted/V2 agents specifically so this case is detectable).
    const agentsMissingMessage = findAgentActionsMissingMessage(
      cleanWorkflowForSerialization(workflow, {}).definition,
    );
    if (agentsMissingMessage.length > 0) {
      const names = agentsMissingMessage.map((n) => `"${n}"`).join(', ');
      const errorMsg =
        agentsMissingMessage.length > 1
          ? `These agents need at least one non-empty prompt message (System instructions or a User message, depending on the agent type) before publishing: ${names}.`
          : `Agent ${names} needs at least one non-empty prompt message (System instructions or a User message, depending on the agent type) before publishing.`;
      console.error('[usePublishDraft] Publish pre-condition failed:', errorMsg);
      telemetryService.trackEvent('workflow.draft.publish_precondition_failed', { error: 'agent_missing_message' });
      onError?.(new Error(errorMsg), { wasRepublish });
      return {
        success: false,
        error: errorMsg,
      };
    }

    try {
      return await mutation.mutateAsync({ wasRepublish });
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      };
    }
  }, [workflow, selectedApp, selectedProject, onError, mutation]);

  return {
    publish,
    isPublishing: mutation.isPending,
    error,
  };
}
